import type {
  FunctionAssignment,
  ProjectFunctionDay,
  ProjectFunctionSeries,
} from "@/hooks/useProjectPeople";

/** Ids of virtual functions start with this prefix (never valid UUIDs). */
const VIRTUAL_PREFIX = "virtual:";
export const VIRTUAL_DRIVER_SERIES_ID = `${VIRTUAL_PREFIX}vehicle-driver`;
export const VIRTUAL_DRIVER_FUNCTION_NAME = "Driver";

export function isVirtualFunctionId(id: string) {
  return id.startsWith(VIRTUAL_PREFIX);
}

type Person = { id: string; firstName: string; lastName: string } | null;

/** The subset of a vehicle booking the driver function is derived from */
export interface DriverBooking {
  id: string;
  vehicleID: string;
  category: string;
  /** Zero date numbers (UTC midnight) */
  startDate: number;
  endDate: number;
  outboundDate?: number | null;
  returnDate?: number | null;
  /** Outbound trip driver (outbound date) */
  driverPersonID?: string | null;
  driver?: Person;
  /** Return trip driver (return date) */
  returnDriverPersonID?: string | null;
  returnDriver?: Person;
  vehicle?: { name: string } | null;
}

const isoDay = (value: number) => new Date(value).toISOString().slice(0, 10);

/** Outbound and return trip: date and driver */
export function driverTrips(booking: DriverBooking) {
  return [
    {
      date: isoDay(booking.outboundDate ?? booking.startDate),
      personId: booking.driverPersonID ?? null,
      person: booking.driver ?? null,
    },
    {
      date: isoDay(booking.returnDate ?? booking.endDate),
      personId: booking.returnDriverPersonID ?? null,
      person: booking.returnDriver ?? null,
    },
  ];
}

/**
 * The project's virtual "Driver" function: one day per outbound/return
 * day of its vehicle bookings, needing one driver per vehicle travelling
 * that day. The outbound driver is assigned on the outbound date, the return
 * driver on the return date, each with the vehicle as badge.
 * Returns null when the project has no vehicle bookings.
 */
export function buildVirtualDriverSeries(
  projectId: string,
  bookings: DriverBooking[],
): ProjectFunctionSeries | null {
  const projectBookings = bookings.filter(
    (booking) => booking.category === "project",
  );
  if (projectBookings.length === 0) return null;

  const days = new Map<string, ProjectFunctionDay>();
  const dayFor = (date: string) => {
    let day = days.get(date);
    if (!day) {
      day = {
        id: `${VIRTUAL_DRIVER_SERIES_ID}:${date}`,
        project_id: projectId,
        series_id: VIRTUAL_DRIVER_SERIES_ID,
        function_date: date,
        required_count_override: 0,
        created_at: "",
        updated_at: "",
        assignments: [] as FunctionAssignment[],
      };
      days.set(date, day);
    }
    return day;
  };

  for (const booking of projectBookings) {
    const trips = driverTrips(booking);
    // One vehicle needs one driver per day, even for a same-day return
    for (const date of new Set(trips.map((trip) => trip.date))) {
      dayFor(date).required_count_override! += 1;
    }
    for (const trip of trips) {
      if (!trip.personId) continue;
      const day = dayFor(trip.date);
      const id = `${day.id}:${booking.id}:${trip.personId}`;
      if (day.assignments!.some((assignment) => assignment.id === id)) continue;
      day.assignments!.push({
        id,
        function_id: day.id,
        person_id: trip.personId,
        remark: null,
        created_at: "",
        updated_at: "",
        person: trip.person
          ? {
              id: trip.person.id,
              first_name: trip.person.firstName,
              last_name: trip.person.lastName,
            }
          : undefined,
        vehicle_name: booking.vehicle?.name ?? null,
        vehicle_id: booking.vehicleID,
      });
    }
  }

  const sortedDays = [...days.values()].sort((a, b) =>
    a.function_date.localeCompare(b.function_date),
  );

  return {
    id: VIRTUAL_DRIVER_SERIES_ID,
    project_id: projectId,
    project_function_group_id: null,
    name: VIRTUAL_DRIVER_FUNCTION_NAME,
    required_count: Math.max(
      ...sortedDays.map((day) => day.required_count_override ?? 1),
    ),
    selected_days: sortedDays.map((day) => day.function_date),
    system_tag: "vehicle_driver",
    location_type: null,
    created_at: "",
    updated_at: "",
    days: sortedDays,
    is_virtual: true,
  };
}
